import {createContext, ReactElement, ReactNode, useCallback, useContext, useRef, useState} from 'react';
import {
    CanvasNavigateFunction,
    CanvasSearchFunction,
    DeletedWorkspaceObject,
    ISAMNode,
    SelectedWorkspaceObject,
    WorkspaceContextProps,
    WorkspaceResource
} from '../@types/workspace-types';
import {ResourceType} from '../@types/resource-type';
import {OccurrenceCategory, PhaseOfFlight} from '../@types/event-sequence-summary';
import {ResourceAction} from '../@types/resource-action';
import {QueryCanvasAction} from '../@types/canvas-action-type';
import {ChatBotAction} from '../@types/chatbot-action-type';
import {FaultTreeViewType} from '../@types/fault-tree-view-type';

// Resource update event types
export interface ResourceUpdateEvent {
    type: ResourceType;
    id: string | number;
    updatedData: any;
    source: 'save' | 'delete' | 'create' | 'refresh';
    timestamp: number;
}

export type ResourceUpdateListener = (event: ResourceUpdateEvent) => void;

export const WorkspaceContext = createContext<WorkspaceContextProps>({} as WorkspaceContextProps);
export const useWorkspaceContext = (): WorkspaceContextProps => useContext(WorkspaceContext);

export const WorkspaceProvider = ({ children }: { children: ReactNode }): ReactElement => {
    const [workspaceObjects, setWorkspaceObjects] = useState<WorkspaceResource|null>(null);
    const [currentWorkspaceObject, setCurrentWorkspaceObject] = useState<SelectedWorkspaceObject|null>(null);
    const [targetGroup, setTargetGroup] = useState<number | null>(null);
    const [currentAction, setCurrentAction] = useState<ResourceAction|null>(null);
    const [additionalResources, setAdditionalResources] = useState<WorkspaceResource[]>([]);
    const [deletedWorkspaceObject, setDeletedWorkspaceObject] = useState<DeletedWorkspaceObject | null>(null);
    const [occurrenceCategories, setOccurrenceCategories] = useState<OccurrenceCategory[] | null>(null);
    const [pedigrees, setPedigrees] = useState<string[] | null>(null);
    const [occurrenceTypes, setOccurrenceTypes] = useState<string[] | null>(null);
    const [phasesOfFlight, setPhasesOfFlight] = useState<PhaseOfFlight[] | null>(null);
    const [currentCanvasAction, setCurrentCanvasAction] = useState<QueryCanvasAction | null>(null);
    const [currentCanvasSelectedNode, setCurrentCanvasSelectedNode] = useState<ISAMNode | null>(null);
    const [chatBotAction, setChatBotAction] = useState<ChatBotAction | null>(null);

    // Canvas search functions (registered by active canvas editor)
    const [canvasSearchFunction, setCanvasSearchFunction] = useState<CanvasSearchFunction | null>(null);
    const [canvasSearchByTagFunction, setCanvasSearchByTagFunction] = useState<CanvasSearchFunction | null>(null);
    const [canvasNavigateFunction, setCanvasNavigateFunction] = useState<CanvasNavigateFunction | null>(null);

    // Lazy load configuration for fault trees (persisted to localStorage)
    const [lazyLoadDepth, setLazyLoadDepth] = useState<number>(() => {
        const saved = localStorage.getItem('ftLazyLoadDepth');
        return saved ? parseInt(saved, 10) : 3;
    });
    const [loadAllFT, setLoadAllFT] = useState<boolean>(() => {
        return localStorage.getItem('ftLoadAll') === 'true';
    });

    // Fault tree view type (persisted to localStorage)
    const [currentFtViewType, setCurrentFtViewType] = useState<FaultTreeViewType>(() => {
        const saved = localStorage.getItem('ftViewType');
        return (saved as FaultTreeViewType) || FaultTreeViewType.STANDARD;
    });

    // Event system state
    const [lastResourceUpdate, setLastResourceUpdate] = useState<ResourceUpdateEvent | null>(null);
    const listenersRef = useRef<Map<ResourceType, Set<ResourceUpdateListener>>>(new Map());

    // Pending group deletes — keyed by group name (available in SSE payload) so the
    // SSE completion handler can look up the group ID and children for tab cleanup.
    const pendingGroupDeletesRef = useRef<Map<string, { id: number; children: number[] }>>(new Map());

    const registerPendingGroupDelete = useCallback((id: number, name: string, children: number[]): void => {
        pendingGroupDeletesRef.current.set(name, { id, children });
    }, []);

    const consumePendingGroupDelete = useCallback((groupName: string): { id: number; children: number[] } | undefined => {
        const entry = pendingGroupDeletesRef.current.get(groupName);
        if (entry) {
            pendingGroupDeletesRef.current.delete(groupName);
        }
        return entry;
    }, []);

    const addOrUpdateAdditionalResource = useCallback((resource: WorkspaceResource): void => {
        setAdditionalResources(prev =>
            prev.filter((r) => r.objectType !== resource.objectType).concat(resource)
        );
    }, []);

    function getAdditionalResource(objectType: ResourceType | undefined): WorkspaceResource {
        if (!objectType) {
            return { objectType: ResourceType.GROUPS };
        } else {
            return additionalResources.find((resource) => resource.objectType === objectType) || { objectType: objectType };
        }

    }

    function deleteAdditionalResource(objectType: ResourceType): void {
        setAdditionalResources(additionalResources.filter((resource) => resource.objectType !== objectType));
    }

    // Subscribe to resource updates
    const subscribeToResourceUpdates = useCallback((
        resourceType: ResourceType,
        listener: ResourceUpdateListener
    ): (() => void) => {
        if (!listenersRef.current.has(resourceType)) {
            listenersRef.current.set(resourceType, new Set());
        }
        listenersRef.current.get(resourceType)!.add(listener);

        // Return unsubscribe function
        return () => {
            const listeners = listenersRef.current.get(resourceType);
            if (listeners) {
                listeners.delete(listener);
                if (listeners.size === 0) {
                    listenersRef.current.delete(resourceType);
                }
            }
        };
    }, []);

    // Publish resource update
    const publishResourceUpdate = useCallback((event: Omit<ResourceUpdateEvent, 'timestamp'>) => {
        const fullEvent: ResourceUpdateEvent = { ...event, timestamp: Date.now() };

        // Update state (triggers re-render for components that need it)
        setLastResourceUpdate(fullEvent);

        // Notify all listeners for this resource type
        const listeners = listenersRef.current.get(event.type);

        if (listeners) {
            listeners.forEach(listener => {
                listener(fullEvent);
            });
        }
    }, []);

    return (
        <WorkspaceContext.Provider value={{
            workspaceObjects, setWorkspaceObjects,
            currentWorkspaceObject, setCurrentWorkspaceObject,
            targetGroup, setTargetGroup,
            currentAction: currentAction, setCurrentAction: setCurrentAction,
            currentCanvasAction, setCurrentCanvasAction,
            chatBotAction, setChatBotAction,
            currentCanvasSelectedNode, setCurrentCanvasSelectedNode,
            deletedWorkspaceObject, setDeletedWorkspaceObject,
            additionalResources, setAdditionalResources,
            addOrUpdateAdditionalResource, getAdditionalResource, deleteAdditionalResource,
            occurrenceCategories, setOccurrenceCategories,
            occurrenceTypes, setOccurrenceTypes,
            pedigrees, setPedigrees,
            phasesOfFlight, setPhasesOfFlight,
            // Event system
            lastResourceUpdate,
            subscribeToResourceUpdates,
            publishResourceUpdate,
            // Canvas search functions
            canvasSearchFunction, setCanvasSearchFunction,
            canvasSearchByTagFunction, setCanvasSearchByTagFunction,
            canvasNavigateFunction, setCanvasNavigateFunction,
            // Lazy load configuration
            lazyLoadDepth, setLazyLoadDepth,
            loadAllFT, setLoadAllFT,
            // Fault tree view type
            currentFtViewType, setCurrentFtViewType,
            // Pending group deletes
            registerPendingGroupDelete,
            consumePendingGroupDelete
        }}>
            {children}
        </WorkspaceContext.Provider>
    );
};
